import { formatMoney, formatNumber, formatTime } from '../../utils/datetime';

/**
 * What the reminder screens share: the page (compose, history, rules), the rule editor and the
 * student page's quick action. Pure helpers and constants; no React.
 */

/**
 * Whether a promo code in a message can actually be redeemed. It cannot yet: the platform's checkout
 * has no promo field and the backend never applies `couponCode` (KB task
 * wire-coupon-codes-into-checkout-or-remove-couponcode), so a code in a message would mislead
 * students. While this is false the promo picker is left out of the compose form, the rule editor and
 * the student page's quick action, the {code} chip is not offered, and the discounts are not even
 * read. The data flow stays (api/reminders.js takes `discountId`; the campaign page still shows the
 * code a campaign carries): turning promo codes on again is this one line.
 */
export const PROMO_REDEEMABLE = false;

export const TASHKENT = 'Asia/Tashkent';
const TASHKENT_OFFSET = '+05:00'; // Uzbekistan has no daylight saving
const TASHKENT_OFFSET_MS = 5 * 60 * 60 * 1000;
const DAY_MS = 24 * 60 * 60 * 1000;

// IN_APP is always delivered; the other four leave the building (the server holds them to the sending hours).
export const CHANNELS = ['IN_APP', 'PUSH', 'TELEGRAM', 'SMS', 'EMAIL'];
// What a new message goes through unless the person unticks it: the same four an automatic rule starts with.
export const DEFAULT_CHANNELS = ['IN_APP', 'PUSH', 'TELEGRAM', 'SMS'];
// Where GET /crm/reminders/channels reports each channel.
export const CHANNEL_FLAG = { IN_APP: 'inApp', PUSH: 'push', TELEGRAM: 'telegram', SMS: 'sms', EMAIL: 'email' };

export const AUDIENCES = ['NO_COURSE', 'UNPAID', 'NOT_STARTED', 'INACTIVE'];
export const INACTIVE_DAYS = [3, 7, 14];
export const PLACEHOLDERS = ['name', 'course', 'link', 'code'];

// Limits the form keeps to, so the server never has to refuse a long text.
export const TITLE_MAX = 120;
export const MESSAGE_MAX = 1000;
// An SMS of up to 160 Latin characters is one part; longer is billed as several.
export const SMS_PART = 160;

// External channels go out between these Tashkent hours (the server holds them until 09:00).
export const SEND_FROM_HOUR = 9;
export const SEND_UNTIL_HOUR = 21;

/* ── Channels ──────────────────────────────────────────────────────────────────────────────── */

/**
 * Whether a channel can be used. `channels` is the answer of GET /crm/reminders/channels, or null
 * when it could not be read: then nothing is blocked (the server skips what it cannot send).
 */
export function channelAvailable(channels, key) {
  if (key === 'IN_APP') return true;
  if (!channels) return true;
  return channels[CHANNEL_FLAG[key]] === true;
}

/** A list of channels in the standard order, IN_APP always in, anything unavailable out. */
export function normalizeChannels(list, channels) {
  const wanted = new Set(['IN_APP', ...(Array.isArray(list) ? list : [])]);
  return CHANNELS.filter((key) => wanted.has(key) && channelAvailable(channels, key));
}

/** The channels a new message starts with. */
export function defaultChannelList(channels) {
  return normalizeChannels(DEFAULT_CHANNELS, channels);
}

/* ── Time (Tashkent) ───────────────────────────────────────────────────────────────────────── */

/** The hour (0-23) it is in Tashkent at `date`; null when `date` is not a date. */
export function tashkentHour(date = new Date()) {
  const hour = Number(String(formatTime(date, { timeZone: TASHKENT })).slice(0, 2));
  return Number.isInteger(hour) ? hour : null;
}

/** True while external channels may send: 09:00 up to 21:00 Tashkent. */
export function insideSendWindow(hour) {
  return Number.isInteger(hour) && hour >= SEND_FROM_HOUR && hour < SEND_UNTIL_HOUR;
}

/** The hour of an "HH:MM" string, or null. */
export function hourOfTime(hhmm) {
  const match = /^(\d{1,2}):(\d{2})$/.exec(String(hhmm || ''));
  if (!match) return null;
  const hour = Number(match[1]);
  return hour >= 0 && hour < 24 ? hour : null;
}

/**
 * The next 09:00 Tashkent time at or after `at` (a Date): when a message asked for outside the
 * sending hours really goes out (the server moves it there).
 */
export function nextSendingStart(at) {
  const local = new Date(at.getTime() + TASHKENT_OFFSET_MS);
  const nine = Date.UTC(local.getUTCFullYear(), local.getUTCMonth(), local.getUTCDate(), SEND_FROM_HOUR) - TASHKENT_OFFSET_MS;
  return new Date(nine > at.getTime() ? nine : nine + DAY_MS);
}

/**
 * A day ("YYYY-MM-DD") and a time ("HH:MM") the staff picked, read as Tashkent time, as a Date.
 * The pickers have no zone of their own, and the sending hours are Tashkent's, so a laptop set to
 * another zone still means 10:00 in Tashkent. null for an incomplete or impossible pair.
 */
export function tashkentInstant(ymd, hhmm) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(ymd || ''))) return null;
  const match = /^(\d{1,2}):(\d{2})$/.exec(String(hhmm || ''));
  if (!match) return null;
  const at = new Date(`${ymd}T${match[1].padStart(2, '0')}:${match[2]}:00.000${TASHKENT_OFFSET}`);
  return Number.isNaN(at.getTime()) ? null : at;
}

/* ── Text ──────────────────────────────────────────────────────────────────────────────────── */

/**
 * Put `token` into `value` in place of the selection [start, end). Returns { value, caret }, or
 * null when the result would not fit `maxLength`.
 */
export function insertToken(value, token, start, end, maxLength = MESSAGE_MAX) {
  const text = String(value || '');
  const from = Number.isInteger(start) ? Math.max(0, Math.min(start, text.length)) : text.length;
  const to = Number.isInteger(end) ? Math.max(from, Math.min(end, text.length)) : from;
  const next = `${text.slice(0, from)}${token}${text.slice(to)}`;
  if (next.length > maxLength) return null;
  return { value: next, caret: from + token.length };
}

const TOKEN_SPLIT = new RegExp(`(\\{(?:${PLACEHOLDERS.join('|')})\\})`);

/** A template as parts: [{ text: 'Salom, ' }, { token: 'name' }, { text: '!' }]. */
export function splitTemplate(template) {
  return String(template || '')
    .split(TOKEN_SPLIT)
    .filter((part) => part !== '')
    .map((part) => {
      const match = /^\{(\w+)\}$/.exec(part);
      return match && PLACEHOLDERS.includes(match[1]) ? { token: match[1] } : { text: part };
    });
}

/** True when the template carries `{name}`-style token `key`. */
export function hasToken(template, key) {
  return String(template || '').includes(`{${key}}`);
}

/* ── Promo codes ───────────────────────────────────────────────────────────────────────────── */

/** A promo code a message may carry: switched on, started, not expired, not used up. */
export function isUsablePromo(discount, now = Date.now()) {
  if (!discount || !discount.code || discount.isActive === false) return false;
  if (discount.endsAt && new Date(discount.endsAt).getTime() < now) return false;
  if (discount.startsAt && new Date(discount.startsAt).getTime() > now) return false;
  if (discount.maxUses && (discount.usedCount || 0) >= discount.maxUses) return false;
  return true;
}

/** "SALE10 · 10%" · "WELCOME · 50 000 so'm". */
export function promoLabel(discount, lang) {
  const value = Number(discount.value);
  const off = discount.type === 'PERCENTAGE'
    ? `${formatNumber(value, { lang })}%`
    : formatMoney(value, 'UZS', { lang, free: false });
  return `${discount.code} · ${off}`;
}

/* ── Results ───────────────────────────────────────────────────────────────────────────────── */

/**
 * What "converted" means for a reminder about this audience: the person enrolled (an invitation or
 * a payment reminder) or came back to a lesson (a study reminder).
 */
export function conversionKind(audience) {
  return audience === 'NOT_STARTED' || audience === 'INACTIVE' ? 'returned' : 'enrolled';
}

/** converted / sent as a percentage (0 when nothing was sent). */
export function conversionRate(sent, converted) {
  const total = Number(sent) || 0;
  if (total <= 0) return 0;
  return Math.round(((Number(converted) || 0) / total) * 1000) / 10;
}

/* ── The nine automatic rules ──────────────────────────────────────────────────────────────── */

// In the order the page lists them; the words are in i18n (reminders.rules.items.<KEY>).
export const RULES = [
  { key: 'NO_COURSE_1D', audience: 'NO_COURSE', tone: 'brand' },
  { key: 'NO_COURSE_3D', audience: 'NO_COURSE', tone: 'brand' },
  { key: 'NO_COURSE_7D', audience: 'NO_COURSE', tone: 'brand' },
  { key: 'UNPAID_1H', audience: 'UNPAID', tone: 'warning' },
  { key: 'UNPAID_24H', audience: 'UNPAID', tone: 'warning' },
  { key: 'NOT_STARTED_2D', audience: 'NOT_STARTED', tone: 'info' },
  { key: 'INACTIVE_3D', audience: 'INACTIVE', tone: 'coral' },
  { key: 'INACTIVE_7D', audience: 'INACTIVE', tone: 'coral' },
  { key: 'INACTIVE_14D', audience: 'INACTIVE', tone: 'coral' },
];

const RULE_BY_KEY = Object.fromEntries(RULES.map((rule) => [rule.key, rule]));

/** The audience and tone of a rule key; a key this page does not know gets a neutral one. */
export function ruleMeta(key) {
  return RULE_BY_KEY[key] || { key, audience: null, tone: 'neutral' };
}

/** The rules the API returned, in the page's order; keys the page does not know come last. */
export function sortRules(rules) {
  const order = new Map(RULES.map((rule, index) => [rule.key, index]));
  return [...rules].sort((a, b) => (order.has(a.key) ? order.get(a.key) : 99) - (order.has(b.key) ? order.get(b.key) : 99));
}
